import { useEffect, useMemo, useState } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { useRequireSuperAdmin } from '../../lib/useRequireSuperAdmin';
import { api, type SuperLink } from '../../lib/api';
import { relativeTime, stripScheme } from '../../lib/dashboardHelpers';
import { C2Button } from '../../components/c2';

export default function AdminLinksPage() {
  const gate = useRequireSuperAdmin();
  const [links, setLinks] = useState<SuperLink[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState<SuperLink | null>(null);

  const load = () => {
    api
      .superLinks()
      .then(setLinks)
      .catch(err => setError((err as Error).message || 'Failed to load links'));
  };

  useEffect(() => {
    if (!gate.ready) return;
    load();
  }, [gate.ready]);

  const filtered = useMemo(() => {
    if (!links) return null;
    if (!filter.trim()) return links;
    const q = filter.toLowerCase();
    return links.filter(
      l =>
        l.slug.toLowerCase().includes(q) ||
        l.url.toLowerCase().includes(q) ||
        (l.workspace_domain ?? '').toLowerCase().includes(q) ||
        (l.creator_email ?? '').toLowerCase().includes(q),
    );
  }, [links, filter]);

  if (!gate.ready) return null;

  return (
    <AdminShell
      title="Links"
      subtitle="500 most recent go/ links across every workspace. Edit to fix broken URLs."
    >
      {error && (
        <div
          style={{
            padding: 14,
            marginBottom: 16,
            background: 'var(--color-peach)',
            color: 'var(--color-peach-deep)',
            borderRadius: 12,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <div
        className="bg-surface overflow-hidden"
        style={{
          border: '1px solid var(--color-hairline)',
          borderRadius: 16,
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <div
          className="flex items-center bg-surface"
          style={{
            gap: 12,
            padding: '14px 20px',
            borderBottom: '1px solid var(--color-hairline)',
          }}
        >
          <input
            placeholder="Filter by slug, url, workspace, or creator…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="flex-1 outline-none text-[13px] text-ink min-w-0"
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-hairline)',
              borderRadius: 10,
              background: 'var(--color-bg-alt)',
            }}
          />
          <span className="text-xs text-ink-muted whitespace-nowrap">
            {filtered ? (
              <>
                <b className="font-semibold text-ink-soft">{filtered.length}</b> of{' '}
                {links?.length ?? 0}
              </>
            ) : (
              '…'
            )}
          </span>
        </div>

        <div
          className="grid bg-bg-alt"
          style={{
            gridTemplateColumns: '180px 1.5fr 1fr 1fr 70px 110px 80px',
            gap: 12,
            padding: '10px 20px',
            borderBottom: '1px solid var(--color-hairline)',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--color-ink-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          <div>Slug</div>
          <div>Destination</div>
          <div>Workspace</div>
          <div>Creator</div>
          <div>Clicks</div>
          <div>Created</div>
          <div />
        </div>

        {!filtered ? (
          <div className="text-ink-soft text-sm" style={{ padding: 40, textAlign: 'center' }}>
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-ink-muted text-sm" style={{ padding: 40, textAlign: 'center' }}>
            No links match.
          </div>
        ) : (
          filtered.map(l => (
            <div
              key={l.id}
              className="grid items-center"
              style={{
                gridTemplateColumns: '180px 1.5fr 1fr 1fr 70px 110px 80px',
                gap: 12,
                padding: '10px 20px',
                borderBottom: '1px solid var(--color-hairline)',
                background: 'var(--color-surface)',
              }}
            >
              <span className="font-mono text-sm font-semibold text-ink overflow-hidden text-ellipsis whitespace-nowrap">
                go/{l.slug}
              </span>
              <a
                href={l.url}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-xs no-underline overflow-hidden text-ellipsis whitespace-nowrap"
                style={{ color: 'var(--color-ink-soft)' }}
                title={l.url}
              >
                {stripScheme(l.url)}
              </a>
              <span className="text-xs text-ink-muted overflow-hidden text-ellipsis whitespace-nowrap font-mono">
                {l.workspace_domain ?? '—'}
              </span>
              <span className="text-xs text-ink-muted overflow-hidden text-ellipsis whitespace-nowrap">
                {l.creator_email ?? '—'}
              </span>
              <span className="text-sm tabular-nums">{l.click_count}</span>
              <span className="text-xs text-ink-muted">{relativeTime(l.created_at)}</span>
              <div>
                <button
                  onClick={() => setEditing(l)}
                  className="text-xs font-semibold cursor-pointer bg-transparent border-0"
                  style={{ color: 'var(--accent-deep)' }}
                >
                  Edit
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <EditLinkModal
        link={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    </AdminShell>
  );
}

interface EditLinkModalProps {
  link: SuperLink | null;
  onClose: () => void;
  onSaved: () => void;
}

function EditLinkModal({ link, onClose, onSaved }: EditLinkModalProps) {
  const [slug, setSlug] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (link) {
      setSlug(link.slug);
      setUrl(link.url);
      setDescription(link.description ?? '');
      setError(null);
    }
  }, [link]);

  if (!link) return null;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.superUpdateLink(link.id, { slug, url, description });
      onSaved();
    } catch (err) {
      setError((err as Error).message || 'Save failed');
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={() => { if (!busy) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(20, 18, 35, 0.42)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-surface"
        style={{
          width: '100%',
          maxWidth: 520,
          borderRadius: 20,
          boxShadow: 'var(--shadow-lg)',
          padding: 28,
        }}
      >
        <h2
          className="font-display font-bold text-ink m-0 mb-1"
          style={{ fontSize: 22, letterSpacing: '-0.01em' }}
        >
          Edit link
        </h2>
        <p className="text-sm text-ink-soft m-0 mb-4">
          In workspace <span className="font-mono">{link.workspace_domain ?? '—'}</span> · created
          by <span className="font-mono">{link.creator_email ?? '—'}</span>
        </p>

        <Field label="Slug">
          <input
            value={slug}
            onChange={e => setSlug(e.target.value)}
            className="w-full font-mono outline-none"
            style={inputStyle}
            autoComplete="off"
            spellCheck={false}
          />
        </Field>

        <Field label="URL">
          <input
            value={url}
            onChange={e => setUrl(e.target.value)}
            className="w-full font-mono outline-none"
            style={inputStyle}
            autoComplete="off"
            spellCheck={false}
          />
        </Field>

        <Field label="Description">
          <input
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="w-full outline-none"
            style={inputStyle}
            autoComplete="off"
          />
        </Field>

        {error && (
          <div
            className="mt-3 text-[13px]"
            style={{
              color: 'var(--color-peach-deep)',
              background: 'var(--color-peach)',
              padding: '8px 12px',
              borderRadius: 10,
            }}
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <C2Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </C2Button>
          <C2Button
            variant="accent"
            size="sm"
            onClick={() => { void save(); }}
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </C2Button>
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '10px 14px',
  border: '1px solid var(--color-hairline-strong)',
  borderRadius: 12,
  fontSize: 14,
  background: 'var(--color-surface)',
  color: 'var(--color-ink)',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label className="text-[12.5px] font-semibold text-ink block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
